import { useState, useEffect } from 'react'
import { X, Phone, Send, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Button from '@/components/ui/Button'
import { useSubmitConsultation } from '@/features/consultation/api'
import { useAuthStore } from '@/stores/authStore'
import { trackRequestConsultation } from '@/lib/analytics'

interface Props {
  isOpen: boolean
  onClose: () => void
  facilityId: number
  facilityName: string
  /** 부모님 정보에서 자동 채울 요양등급 */
  defaultCareGrade?: string
  /** 사용자 이름 자동 채움 */
  defaultName?: string
  /** 사용자 전화번호 자동 채움 */
  defaultPhone?: string
}

const CARE_GRADE_OPTIONS = [
  { value: '', label: '선택 안함' },
  { value: 'GRADE_1', label: '1등급' },
  { value: 'GRADE_2', label: '2등급' },
  { value: 'GRADE_3', label: '3등급' },
  { value: 'GRADE_4', label: '4등급' },
  { value: 'GRADE_5', label: '5등급' },
  { value: 'GRADE_COGNITIVE', label: '인지지원등급' },
]

export default function ConsultationModal({
  isOpen,
  onClose,
  facilityId,
  facilityName,
  defaultCareGrade,
  defaultName,
  defaultPhone,
}: Props) {
  const { isAuthenticated } = useAuthStore()
  const { mutate: submit, isPending } = useSubmitConsultation()

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [careGrade, setCareGrade] = useState('')
  const [message, setMessage] = useState('')
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setName(defaultName ?? '')
      setPhone(defaultPhone ?? '')
      setCareGrade(defaultCareGrade ?? '')
      setMessage('')
      setSubmitted(false)
    }
  }, [isOpen, defaultName, defaultPhone, defaultCareGrade])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    submit(
      {
        facilityId,
        guardianName: name,
        guardianPhone: phone,
        careGrade: careGrade || undefined,
        message: message || undefined,
      },
      {
        onSuccess: () => {
          trackRequestConsultation(facilityId)
          setSubmitted(true)
        },
      }
    )
  }

  const isValid = name.trim().length > 0 && phone.trim().length >= 10

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="w-full max-w-lg rounded-t-3xl bg-white px-5 pb-8 pt-4"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 헤더 */}
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-cb-gray-900">입소 상담 요청</h2>
              <button onClick={onClose} className="p-1 text-cb-gray-400 hover:text-cb-gray-600">
                <X size={22} />
              </button>
            </div>

            {/* 비로그인 안내 */}
            {!isAuthenticated && (
              <div className="rounded-xl bg-amber-50 p-4 mb-5 text-body-sm text-amber-700">
                로그인하시면 상담 요청 내역을 관리할 수 있습니다.
              </div>
            )}

            {submitted ? (
              /* 완료 화면 */
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <Send size={28} className="text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-cb-gray-900">상담 요청 완료!</h3>
                <p className="text-body-sm text-cb-gray-500 text-center">
                  <strong>{facilityName}</strong>에 입소 상담 요청이<br />
                  접수되었습니다. 빠른 시일 내 연락드리겠습니다.
                </p>
                <Button variant="primary" fullWidth onClick={onClose} className="mt-2">
                  확인
                </Button>
              </div>
            ) : (
              /* 입력 폼 */
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <p className="text-body-sm text-cb-gray-500 -mt-2 mb-1">
                  <strong className="text-cb-gray-800">{facilityName}</strong>에 입소 상담을 요청합니다.
                </p>

                {/* 보호자 이름 */}
                <div>
                  <label className="block text-body-sm font-medium text-cb-gray-700 mb-1">
                    보호자 이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="홍길동"
                    className="w-full rounded-xl border border-cb-gray-200 px-4 py-3 text-body-sm focus:border-cb-primary focus:outline-none"
                    maxLength={50}
                    required
                  />
                </div>

                {/* 연락처 */}
                <div>
                  <label className="block text-body-sm font-medium text-cb-gray-700 mb-1">
                    연락처 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="010-1234-5678"
                    className="w-full rounded-xl border border-cb-gray-200 px-4 py-3 text-body-sm focus:border-cb-primary focus:outline-none"
                    maxLength={20}
                    required
                  />
                </div>

                {/* 요양등급 */}
                <div>
                  <label className="block text-body-sm font-medium text-cb-gray-700 mb-1">
                    대상자 요양등급
                  </label>
                  <select
                    value={careGrade}
                    onChange={(e) => setCareGrade(e.target.value)}
                    className="w-full rounded-xl border border-cb-gray-200 px-4 py-3 text-body-sm focus:border-cb-primary focus:outline-none bg-white"
                  >
                    {CARE_GRADE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* 메시지 */}
                <div>
                  <label className="block text-body-sm font-medium text-cb-gray-700 mb-1">
                    문의 내용
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="궁금한 점이나 요청사항을 입력해주세요"
                    className="w-full rounded-xl border border-cb-gray-200 px-4 py-3 text-body-sm focus:border-cb-primary focus:outline-none resize-none"
                    rows={3}
                    maxLength={1000}
                  />
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={!isValid || isPending}
                  className="mt-1"
                >
                  {isPending ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <>
                      <Phone size={18} />
                      상담 요청하기
                    </>
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
